/**
 * Fleet Recent Events — top 3 recent notification events as alert cards.
 * Styled with event-kind-specific colors (warning bg, error bg).
 * Authority: Fleet Dashboard design (Pencil mock) §Recent Events.
 */

import { Megaphone, Wifi } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { NotificationActivityRow } from '@/hooks/useNotificationActivity';

interface FleetRecentEventsProps {
  events: NotificationActivityRow[];
}

const SEVERITY_STYLES: Record<string, { bg: string; text: string; icon: React.ReactNode }> = {
  critical: {
    bg: 'bg-[hsl(var(--color-error))]',
    text: 'text-[hsl(var(--destructive))]',
    icon: <Wifi className="h-5 w-5 text-[hsl(var(--destructive))]" />,
  },
  warning: {
    bg: 'bg-[hsl(var(--color-warning))]',
    text: 'text-wt-amber-primary',
    icon: <Megaphone className="h-5 w-5 text-wt-amber-primary" />,
  },
  info: {
    bg: 'bg-[hsl(var(--color-info))]',
    text: 'text-[hsl(var(--neon-purple))]',
    icon: <Megaphone className="h-5 w-5 text-[hsl(var(--neon-purple))]" />,
  },
};

function getSeverityForEvent(event: NotificationActivityRow): string {
  // Use the severity from the event; fall back to 'warning'
  if (event.severity === 'critical') return 'critical';
  if (event.severity === 'info') return 'info';
  return 'warning';
}

export function FleetRecentEvents({ events }: FleetRecentEventsProps) {
  const recentEvents = events.slice(0, 3);
  const criticalCount = recentEvents.filter((e) => e.severity === 'critical').length;
  const warningCount = recentEvents.filter((e) => e.severity === 'warning').length;

  const badgeText = [
    criticalCount > 0 ? `${criticalCount} critical` : null,
    warningCount > 0 ? `${warningCount} warning` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-mono text-base font-semibold text-foreground">Recent Events</h3>
        {badgeText && (
          <span className="inline-flex items-center rounded-full bg-[hsl(var(--color-warning))] px-2 py-1 text-xs font-mono text-wt-amber-primary">
            {badgeText}
          </span>
        )}
      </div>
      <div className="space-y-3">
        {recentEvents.map((event) => {
          const sev = getSeverityForEvent(event);
          const style = SEVERITY_STYLES[sev] ?? SEVERITY_STYLES.warning;
          return (
            <div
              key={event.id}
              className={cn('flex gap-3 rounded-lg p-4', style.bg)}
            >
              <div className="shrink-0 pt-0.5">{style.icon}</div>
              <div className="min-w-0 flex-1 space-y-1">
                <p className={cn('text-base font-medium', style.text)}>
                  {event.event_kind} — {event.property_name}
                </p>
                <p className={cn('text-base', style.text)}>{event.title}</p>
              </div>
            </div>
          );
        })}
        {recentEvents.length === 0 && (
          <p className="text-sm text-muted-foreground py-4 text-center">No recent events.</p>
        )}
      </div>
    </div>
  );
}
